<template>
  <transition name="sm-modal">
    <div v-if="visible" class="sm-modal-overlay" @click.self="$emit('close')">
      <div class="sm-modal" :class="{ 'sm-modal--phone-full': phoneFull }" role="dialog" aria-modal="true" :style="{ maxWidth: width }">
        <header class="sm-modal__header">
          <h3 class="sm-modal__title">
            <slot name="title" />
          </h3>
          <!-- Необязательное место между названием и крестиком (например, ошибка формы): шапка не прокручивается -->
          <div v-if="$slots['header-extra']" class="sm-modal__extra">
            <slot name="header-extra" />
          </div>
          <button
            type="button"
            class="sm-modal__close"
            aria-label="Закрыть"
            @click="$emit('close')"
          >
            <i class="fas fa-times"></i>
          </button>
        </header>

        <div class="sm-modal__body">
          <slot />
        </div>

        <footer v-if="$slots.footer" class="sm-modal__footer">
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'BaseModal',
  props: {
    visible: { type: Boolean, default: false },
    width: { type: String, default: '400px' },
    // На телефоне окно занимает весь экран (без отступа сверху), а не «шторку» на 92% высоты
    phoneFull: { type: Boolean, default: false }
  },
  mounted() {
    this._onKey = (e) => {
      if (e.key === 'Escape' && this.visible) this.$emit('close')
    }
    document.addEventListener('keydown', this._onKey)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._onKey)
  }
}
</script>

<style scoped>
.sm-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
}

.sm-modal {
  background: var(--bg-surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-modal);
  width: 92%;
  max-width: 90vw;
  max-height: 80vh;
  max-height: 88dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sm-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-12) var(--space-16);
  border-bottom: var(--border-width-default) var(--border-style-base) var(--border-default);
  flex-shrink: 0;
}

.sm-modal__title {
  margin: 0;
  font-size: var(--text-md);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: var(--space-8);
}
.sm-modal__title i {
  color: var(--primary);
}

.sm-modal__extra {
  flex: 1;
  min-width: 0;
  margin: 0 var(--space-10);
}

.sm-modal__close {
  border: none;
  background: var(--bg-subtle);
  color: var(--text-secondary);
  border-radius: var(--radius-md);
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-base), transform var(--duration-medium) var(--ease-spring);
}
.sm-modal__close:hover {
  background: var(--color-neutral-200);
  color: var(--text-primary);
  transform: rotate(90deg);
}
.sm-modal__close:focus-visible { outline: none; box-shadow: var(--focus-ring); }

.sm-modal__body {
  padding: var(--space-16);
  overflow-y: auto;
  flex: 1;
}

.sm-modal__footer {
  padding: var(--space-12) var(--space-16);
  border-top: var(--border-width-default) var(--border-style-base) var(--border-default);
  flex-shrink: 0;
}

/* Transition */
.sm-modal-enter-active,
.sm-modal-leave-active {
  transition: opacity var(--duration-medium) var(--ease-base);
}
.sm-modal-enter-active .sm-modal {
  transition: transform var(--duration-medium) var(--ease-spring), opacity var(--duration-medium) var(--ease-out);
}
.sm-modal-leave-active .sm-modal {
  transition: transform var(--duration-normal) var(--ease-base), opacity var(--duration-normal) var(--ease-base);
}
.sm-modal-enter,
.sm-modal-leave-to {
  opacity: 0;
}
.sm-modal-enter .sm-modal,
.sm-modal-leave-to .sm-modal {
  opacity: 0;
  transform: translateY(18px) scale(0.96);
}

/* Телефон: окно почти во весь экран, содержимое прокручивается внутри */
@media (max-width: 640px) {
  .sm-modal-overlay { align-items: flex-end; }
  .sm-modal {
    width: 100%;
    max-width: 100% !important;
    max-height: 92dvh;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
  .sm-modal__body { -webkit-overflow-scrolling: touch; }
  .sm-modal--phone-full { height: 100dvh; max-height: 100dvh; border-radius: 0; }
  /* окно на весь экран: шапка не прячется под строкой состояния (время, сеть, батарея) и вырезом iPhone, низ — над полосой «домой» */
  .sm-modal--phone-full .sm-modal__header { padding-top: calc(var(--space-12) + env(safe-area-inset-top, 0px)); }
  .sm-modal--phone-full .sm-modal__body { padding-bottom: calc(var(--space-16) + env(safe-area-inset-bottom, 0px)); }
  .sm-modal--phone-full .sm-modal__footer { padding-bottom: calc(var(--space-12) + env(safe-area-inset-bottom, 0px)); }
  /* крестик: удобная зона нажатия пальцем */
  .sm-modal__close { width: 40px; height: 40px; font-size: var(--text-base); }
}
</style>
